import { formatCurrency as formatCurrencyBase } from '@monorepo/client-common';
import type { Trade } from '@monorepo/types';
import { PositionSide } from '@monorepo/types';
import type { ChipProps } from '@mui/material';
import { TAG_COLORS } from '../../../table/constants';

/**
 * Format a number as currency with 2 decimal places
 */
export const formatCurrency = (value: number): string => {
  return formatCurrencyBase(value, {
    currency: 'USD',
    decimalPlaces: 2,
  });
};

/**
 * Get the color for a side tag based on position side
 */
export const getSideTagColor = (side: string): ChipProps['color'] => {
  if (side === PositionSide.Buy) {
    return TAG_COLORS.SUCCESS as ChipProps['color'];
  } else if (side === PositionSide.Correction) {
    return TAG_COLORS.INFO as ChipProps['color'];
  }
  return TAG_COLORS.ERROR as ChipProps['color'];
};

/**
 * Action key type for trade actions
 */
export type TradeActionKey =
  | 'position:trades_table.open'
  | 'position:trades_table.increase'
  | 'position:trades_table.decrease'
  | 'common:close';

/**
 * Get the translation key for a trade action based on its position in the list and trade properties
 * Returns a translation key that should be passed to the t() function
 */
export const getActionKey = (
  trade: Trade,
  index: number,
  totalTrades: number,
  positionState: 'open' | 'closed',
): TradeActionKey => {
  const isFirst = index === 0;
  if (isFirst) {
    return 'position:trades_table.open';
  }

  const isLast = index === totalTrades - 1;
  if (isLast && positionState === 'closed') {
    return 'common:close';
  }

  if (trade.isCover) {
    return 'position:trades_table.decrease';
  }

  return 'position:trades_table.increase';
};

/**
 * Date format constant for trade dates
 */
export const TRADE_DATE_FORMAT = 'MMM D HH:mm:ss' as const;

/**
 * Truncate a trade ID to show only the last N characters with ellipsis
 * @param id - The full trade ID
 * @param length - Number of characters to show (default: 8)
 * @returns Truncated ID with ellipsis prefix
 */
export const truncateTradeId = (id: string, length = 8): string => {
  if (!id || id.length <= length) {
    return id;
  }
  return `...${id.slice(-length)}`;
};
